import type React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { MiniBadge } from '@xing-com/badge';
import { AriaExtendedText } from '@xing-com/crate-jobs-components-aria-extended-text';
import {
  getTimeDifferenceInDays,
  getTimeDifferenceInHours,
} from '@xing-com/crate-jobs-helpers';
import { getActiveUntilTimeDifferenceInDays } from '@xing-com/crate-jobs-helpers/src/job-age';
import { spaceXS } from '@xing-com/tokens';
import type { BodyCopyLineHeight } from '@xing-com/typography';
import { Meta } from '@xing-com/typography';

const Notification = styled(MiniBadge)`
  margin-right: ${spaceXS};
`;

const StyledMeta = styled(Meta)`
  position: relative;
  width: fit-content;
`;

type TranslationValues = {
  a11yId: string;
  id: string;
  showTimeDifference?: boolean;
  values?: Record<string, number>;
};

// negative number to avoid showing it up in case of undefined or null
const INVALID_ACTIVE_UNTIL_DAYS = -1;

const getTranslationValues = ({
  activeUntil,
  date,
}: {
  activeUntil: Props['activeUntil'];
  date: Props['date'];
}): TranslationValues => {
  const dateTimeDifferenceInDays = getTimeDifferenceInDays(date);
  const dateTimeDifferenceInHours = getTimeDifferenceInHours(date);
  const activeUntilTimeDifferenceInDays =
    activeUntil !== null && activeUntil !== undefined
      ? getActiveUntilTimeDifferenceInDays(activeUntil)
      : INVALID_ACTIVE_UNTIL_DAYS;

  if (dateTimeDifferenceInHours < 1) {
    return {
      a11yId: 'JOBS_TIMESTAMP_JUST_NOW_A11Y',
      id: 'JOBS_TIMESTAMP_JUST_NOW',
    };
  }

  if (dateTimeDifferenceInDays < 1) {
    return {
      a11yId: 'JOBS_TIMESTAMP_N_HOURS_AGO_A11Y',
      id: 'JOBS_TIMESTAMP_N_HOURS_AGO',
      values: { number: Math.floor(dateTimeDifferenceInHours) },
    };
  }

  if (dateTimeDifferenceInDays <= 2) {
    return {
      a11yId: 'JOBS_TIMESTAMP_YESTERDAY_A11Y',
      id: 'JOBS_TIMESTAMP_YESTERDAY',
    };
  }

  if (dateTimeDifferenceInDays < 8) {
    return {
      a11yId: 'JOBS_TIMESTAMP_N_DAYS_AGO_A11Y',
      id: 'JOBS_TIMESTAMP_N_DAYS_AGO',
      values: { number: Math.floor(dateTimeDifferenceInDays) },
    };
  }

  if (
    activeUntilTimeDifferenceInDays < 8 &&
    activeUntilTimeDifferenceInDays >= 0
  ) {
    if (activeUntilTimeDifferenceInDays < 1) {
      return {
        a11yId: 'JOBS_DATE_EXPIRING_TODAY_LABEL',
        id: 'JOBS_DATE_EXPIRING_TODAY_LABEL',
      };
    }

    if (activeUntilTimeDifferenceInDays < 2) {
      return {
        a11yId: 'JOBS_DATE_DAYS_LEFT_LABEL.one',
        id: 'JOBS_DATE_DAYS_LEFT_LABEL.one',
        values: { variable_1: Math.floor(activeUntilTimeDifferenceInDays) },
      };
    }

    return {
      a11yId: 'JOBS_DATE_DAYS_LEFT_LABEL.other',
      id: 'JOBS_DATE_DAYS_LEFT_LABEL.other',
      values: { variable_1: Math.floor(activeUntilTimeDifferenceInDays) },
    };
  }

  return {
    a11yId: 'JOBS_TIMESTAMP_30_PLUS_DAYS_AGO_A11Y',
    id: 'JOBS_TIMESTAMP_30_PLUS_DAYS_AGO',
    showTimeDifference: false,
  };
};

type Props = {
  activeUntil?: string | null | undefined;
  date: string;
  isNew?: boolean;
  noMargin?: boolean;
  lineHeight?: BodyCopyLineHeight;
  className?: string;
};

export const OptimisticPublicationDate: React.FC<Props> = ({
  activeUntil,
  date,
  isNew = false,
  ...props
}) => {
  const {
    a11yId,
    id,
    showTimeDifference = true,
    values = {},
  } = getTranslationValues({ date, activeUntil });

  return (
    <StyledMeta size="small" {...props}>
      {isNew ? (
        <>
          <Notification data-testid="mini-badge" />
          <FormattedMessage id="JOBS_NEW_JOB_LABEL" />
        </>
      ) : null}
      {isNew && showTimeDifference ? <>&nbsp;&middot;&nbsp;</> : null}
      {showTimeDifference ? (
        <time dateTime={date}>
          <AriaExtendedText
            item={{ id, values }}
            a11yItem={{ id: a11yId, values }}
            noWrapper
          />
        </time>
      ) : null}
    </StyledMeta>
  );
};
